import config from '../../config';
import { getAuthHeaders, handleResponse, handleNoContentResponse } from './common';

const BASE_URL = () => `${config.api.url}/system-announcements`;
const ADMIN_BASE_URL = () => `${config.api.url}/admin/system-announcements`;

function normalizeAttachment(dto) {
  return {
    referenceId: dto.referenceId ?? dto.ReferenceId,
    fileName: dto.fileName ?? dto.FileName ?? 'Attachment',
    contentType: dto.contentType ?? dto.ContentType ?? '',
    sizeBytes: dto.sizeBytes ?? dto.SizeBytes ?? 0,
  };
}

function normalizeAnnouncement(dto) {
  if (!dto || typeof dto !== 'object') return dto;
  const publishedAt =
    dto.publishAt ??
    dto.PublishAt ??
    dto.publishedAt ??
    dto.PublishedAt ??
    dto.publishDate ??
    dto.PublishDate ??
    dto.createdAt ??
    dto.CreatedAt ??
    null;
  const expiresAt = dto.expiresAt ?? dto.ExpiresAt ?? null;
  const isReadRaw = dto.isRead ?? dto.IsRead;
  const readAt = dto.readAt ?? dto.ReadAt ?? null;
  const dismissedAt = dto.dismissedAt ?? dto.DismissedAt ?? null;
  const status = dto.status ?? dto.Status ?? '';
  const isArchived =
    dto.isArchived ??
    dto.IsArchived ??
    (String(status).toLowerCase() === 'archived');
  // Explicit fields after spread so normalized values win over raw DTO keys.
  const normalized = {
    ...dto,
    referenceId: dto.referenceId ?? dto.ReferenceId,
    title: dto.title ?? dto.Title ?? '',
    summary: dto.summary ?? dto.Summary ?? '',
    body: dto.body ?? dto.Body ?? '',
    category: dto.category ?? dto.Category ?? '',
    priority: dto.priority ?? dto.Priority ?? '',
    publishedAt,
    publishAt: publishedAt,
    expiresAt,
    primaryActionLabel: dto.primaryActionLabel ?? dto.PrimaryActionLabel ?? null,
    primaryActionUrl: dto.primaryActionUrl ?? dto.PrimaryActionUrl ?? null,
    secondaryActionLabel: dto.secondaryActionLabel ?? dto.SecondaryActionLabel ?? null,
    secondaryActionUrl: dto.secondaryActionUrl ?? dto.SecondaryActionUrl ?? null,
    attachments: (dto.attachments?.$values ?? dto.attachments ?? dto.Attachments?.$values ?? dto.Attachments ?? []).map(normalizeAttachment),
    screenshotUrl: dto.screenshotUrl ?? dto.ScreenshotUrl ?? null,
    screenshotPreviewUrl: dto.screenshotPreviewUrl ?? dto.ScreenshotPreviewUrl ?? null,
    targetingMode: dto.targetingMode ?? dto.TargetingMode ?? 'AllOrganizations',
    publishImmediately: Boolean(
      dto.publishImmediately ?? dto.PublishImmediately ?? false
    ),
    status,
    isArchived: Boolean(isArchived),
    archivedAt: dto.archivedAt ?? dto.ArchivedAt ?? null,
    createdAt: dto.createdAt ?? dto.CreatedAt ?? null,
    updatedAt: dto.updatedAt ?? dto.UpdatedAt ?? null,
    isRead: isReadRaw != null ? Boolean(isReadRaw) : undefined,
    readAt,
    dismissedAt,
    isDismissed: Boolean(dismissedAt) || Boolean(dto.isDismissed ?? dto.IsDismissed),
  };
  normalized.publishedAt = publishedAt;
  normalized.publishAt = publishedAt;
  return normalized;
}

function normalizeAnnouncementList(raw) {
  const items = raw?.$values ?? raw?.items ?? raw?.announcements ?? raw;
  if (!Array.isArray(items)) return [];
  return items.map(normalizeAnnouncement);
}

const systemAnnouncementsApi = {
  getActiveAnnouncements: async () => {
    const res = await fetch(`${BASE_URL()}/active`, {
      headers: getAuthHeaders(),
    });
    if (res.status === 204) return [];
    const result = await handleResponse(res);
    return normalizeAnnouncementList(result);
  },

  getAnnouncementArchive: async () => {
    const res = await fetch(`${BASE_URL()}/archive`, {
      headers: getAuthHeaders(),
    });
    if (res.status === 204) return [];
    const result = await handleResponse(res);
    return normalizeAnnouncementList(result);
  },

  dismissAnnouncement: async (referenceId) => {
    const res = await fetch(`${BASE_URL()}/${referenceId}/dismiss`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleNoContentResponse(res);
  },

  markAnnouncementRead: async (referenceId) => {
    const res = await fetch(`${BASE_URL()}/${referenceId}/read`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleNoContentResponse(res);
  },

  adminListAnnouncements: async () => {
    const res = await fetch(ADMIN_BASE_URL(), {
      headers: getAuthHeaders(),
    });
    if (res.status === 204) return [];
    const result = await handleResponse(res);
    return normalizeAnnouncementList(result);
  },

  adminGetAnnouncement: async (referenceId) => {
    const res = await fetch(`${ADMIN_BASE_URL()}/${referenceId}`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(res);
    return normalizeAnnouncement(result);
  },

  adminCreateAnnouncement: async (payload) => {
    const res = await fetch(ADMIN_BASE_URL(), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const result = await handleResponse(res);
    return normalizeAnnouncement(result);
  },

  adminUpdateAnnouncement: async (referenceId, payload) => {
    const res = await fetch(`${ADMIN_BASE_URL()}/${referenceId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const result = await handleResponse(res);
    return normalizeAnnouncement(result);
  },

  adminArchiveAnnouncement: async (referenceId) => {
    const res = await fetch(`${ADMIN_BASE_URL()}/${referenceId}/archive`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleNoContentResponse(res);
  },

  adminUploadAttachment: async (file) => {
    const body = new FormData();
    body.append('file', file);
    const token = localStorage.getItem('velaToken');
    const res = await fetch(`${ADMIN_BASE_URL()}/attachments`, {
      method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body,
    });
    return normalizeAttachment(await handleResponse(res));
  },

  downloadAttachment: async (announcementId, attachment, admin = false) => {
    const id = encodeURIComponent(attachment.referenceId);
    const url = admin ? `${ADMIN_BASE_URL()}/attachments/${id}`
      : `${BASE_URL()}/${encodeURIComponent(announcementId)}/attachments/${id}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) { await handleResponse(res); return; }
    const blobUrl = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = attachment.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  },

  adminUploadScreenshot: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('velaToken');
    const res = await fetch(`${ADMIN_BASE_URL()}/screenshots`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const result = await handleResponse(res);
    return {
      storageKey: result.storageKey ?? result.StorageKey ?? '',
      previewUrl: result.previewUrl ?? result.PreviewUrl ?? '',
    };
  },
};

export default systemAnnouncementsApi;
